/* global bootstrap */
/* global HSCore */

import React, { useState, useEffect, useContext, useRef } from "react";
import LanguageContext from "../utils/Contexts/LanguageContext";

function ProductFilterModal({
  listsData,
  setFormData,
  formData,
  applyFilter,
  selectedFilter,
  setSelectedFilter,
}) {
  const { t } = useContext(LanguageContext);
  const [selectedDomainOptions, setSelectedDomainOptions] = useState([]);
  const [selectedListOptions, setSelectedListOptions] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [publicationType, setPublicationType] = useState("");
  const [minDomainRating, setMinDomainRating] = useState("");
  const [maxDomainRating, setMaxDomainRating] = useState("");
  const [minDomainAuthority, setMinDomainAuthority] = useState("");
  const [maxDomainAuthority, setMaxDomainAuthority] = useState("");
  const [tat, setTat] = useState("");
  const [traffic, setTraffic] = useState("");
  const [linkType, setLinkType] = useState("");
  const [niche, setNiche] = useState("");
  const [verificationStatus, setVerificationStatus] = useState("");
  const [lang, setLang] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [errors, setErrors] = useState({});

  const listOptions = listsData?.map((item) => ({
    value: item.uuid,
    label: item.name,
  }));

  const handleFilterChange = (setter) => (e) => {
    const value = e.target.value;
    const numericValue = value ? Number(value) : undefined;
    setter(numericValue);
    setErrors((prevErrors) => ({ ...prevErrors, [e.target.name]: "" }));
  };

  const handleCategoryChange = (categoryId) => (e) => {
    const { checked } = e.target;
    setSelectedCategories((prevState) => {
      const updatedCategories = checked
        ? [...prevState, categoryId]
        : prevState.filter((id) => id !== categoryId);
      return updatedCategories;
    });
  };

  const handleApplyFilter = () => {
    const validationErrors = {};

    if (minPrice && (minPrice < 1 || minPrice > 9999)) {
      validationErrors.minPrice = "Minimum price should be between 1 and 9999.";
    }
    if (maxPrice && (maxPrice < 1 || maxPrice > 9999)) {
      validationErrors.maxPrice = "Maximum price should be between 1 and 9999.";
    }

    if (minDomainRating && (minDomainRating < 1 || minDomainRating > 100)) {
      validationErrors.minDomainRating =
        "Domain Rating should be between 1 and 100.";
    }
    if (maxDomainRating && (maxDomainRating < 1 || maxDomainRating > 100)) {
      validationErrors.maxDomainRating =
        "Domain Rating should be between 1 and 100.";
    }
    if (
      minDomainAuthority &&
      (minDomainAuthority < 1 || minDomainAuthority > 100)
    ) {
      validationErrors.minDomainAuthority =
        "Domain Authority should be between 1 and 100.";
    }
    if (
      maxDomainAuthority &&
      (maxDomainAuthority < 1 || maxDomainAuthority > 100)
    ) {
      validationErrors.maxDomainAuthority =
        "Domain Authority should be between 1 and 100.";
    }
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const domainFilters = selectedDomainOptions.map((option) => ({
      key: "DOMAIN",
      value: option.value,
    }));
    const listFilters = selectedListOptions.map((option) => ({
      key: "LIST",
      value: option.value,
    }));
    const categoryFilters = selectedCategories.map((option) => ({
      key: "CATEGORY",
      value: option,
    }));
    let filterData = [...domainFilters, ...listFilters, ...categoryFilters];
    minPrice && filterData.push({ key: "PMN", value: minPrice });
    maxPrice && filterData.push({ key: "PMX", value: maxPrice });
    publicationType && filterData.push({ key: "PUB", value: publicationType });
    minDomainRating && filterData.push({ key: "DRMN", value: minDomainRating });
    maxDomainRating && filterData.push({ key: "DRMX", value: maxDomainRating });
    minDomainAuthority &&
      filterData.push({ key: "DAMN", value: minDomainAuthority });
    maxDomainAuthority &&
      filterData.push({ key: "DAMX", value: maxDomainAuthority });
    tat && filterData.push({ key: "TAT", value: tat });
    traffic && filterData.push({ key: "TRAFFIC", value: traffic });
    linkType && filterData.push({ key: "REL", value: linkType });
    niche && filterData.push({ key: "NICHE", value: niche });
    verificationStatus &&
      filterData.push({ key: "VER", value: verificationStatus });
    lang && filterData.push({ key: "LANG", value: lang });

    setFormData((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        filters: filterData,
      },
    }));

    applyFilter();
    const modalElement = document.getElementById(
      "offcanvasEcommerceProductFilter"
    );
    if (modalElement) {
      modalElement.classList.remove("show"); // Removes the 'show' class to hide the modal
    }
    const backdropElement = document.querySelector(".offcanvas-backdrop");
    if (backdropElement) {
      backdropElement.classList.remove("show"); // Remove the backdrop if present
    }
  };

  const handleClearFilters = () => {
    // Reset all state variables
    setSelectedDomainOptions([]);
    setSelectedListOptions([]);
    setMinPrice("");
    setMaxPrice("");
    setPublicationType("");
    setMinDomainRating("");
    setMaxDomainRating("");
    setMinDomainAuthority("");
    setMaxDomainAuthority("");
    setTat("");
    setTraffic("");
    setLinkType("");
    setNiche("");
    setVerificationStatus("");
    setLang("");
    setSelectedCategories([]);

    setSelectedFilter(null);

    // Reset formData
    setFormData((prevState) => ({
      ...prevState,
      search: {
        ...prevState.search,
        filters: [], // Clear all filters
      },
    }));
  };

  const handleClearCategories = () => {
    setSelectedCategories([]);
    setFormData((prevStateFormData) => ({
      ...prevStateFormData,
      search: {
        ...prevStateFormData.search,
        filters: (prevStateFormData.search.filters || []).filter(
          (filter) => filter.key !== "CATEGORY"
        ),
      },
    }));
  };

  useEffect(() => {
    if (!selectedFilter) {
      handleClearFilters();
      return;
    }
    const domains = formData?.search?.filters?.filter(
      (filter) => filter.key === "DOMAIN"
    );
    const lists = formData?.search?.filters?.filter(
      (filter) => filter.key === "LIST"
    );
    const pmn = formData?.search?.filters?.find(
      (filter) => filter.key === "PMN"
    );
    const pmx = formData?.search?.filters?.find(
      (filter) => filter.key === "PMX"
    );
    const pub = formData?.search?.filters?.find(
      (filter) => filter.key === "PUB"
    );
    const drmn = formData?.search?.filters?.find(
      (filter) => filter.key === "DRMN"
    );
    const drmx = formData?.search?.filters?.find(
      (filter) => filter.key === "DRMX"
    );
    const damn = formData?.search?.filters?.find(
      (filter) => filter.key === "DAMN"
    );
    const damx = formData?.search?.filters?.find(
      (filter) => filter.key === "DAMX"
    );
    const tat = formData?.search?.filters?.find(
      (filter) => filter.key === "TAT"
    );
    const traffic = formData?.search?.filters?.find(
      (filter) => filter.key === "TRAFFIC"
    );
    const rel = formData?.search?.filters?.find(
      (filter) => filter.key === "REL"
    );
    const niche = formData?.search?.filters?.find(
      (filter) => filter.key === "NICHE"
    );
    const ver = formData?.search?.filters?.find(
      (filter) => filter.key === "VER"
    );
    const lang = formData?.search?.filters?.find(
      (filter) => filter.key === "LANG"
    );
    const categories = formData?.search?.filters?.filter(
      (filter) => filter.key === "CATEGORY"
    );
    if (domains.length) {
      const domainNames = domains?.map((domain) => domain.value);
      const domainOptions = domainNames?.map((domainName) => ({
        value: domainName,
        label: domainName,
      }));
      setSelectedDomainOptions(domainOptions);
    }
    if (lists.length) {
      const listIds = lists?.map((list) => list.value);
      const filteredLists = listsData?.filter((list) =>
        listIds.includes(list.uuid)
      );
      const listOptions = filteredLists?.map((item) => ({
        value: item.uuid,
        label: item.name,
      }));
      setSelectedListOptions(listOptions);
    }
    pmn && setMinPrice(pmn.value);
    pmx && setMaxPrice(pmx.value);
    pub && setPublicationType(pub.value);
    drmn && setMinDomainRating(drmn.value);
    drmx && setMaxDomainRating(drmx.value);
    damn && setMinDomainAuthority(damn.value);
    damx && setMaxDomainAuthority(damx.value);
    tat && setTat(tat.value);
    traffic && setTraffic(traffic.value);
    rel && setLinkType(rel.value);
    niche && setNiche(niche.value);
    ver && setVerificationStatus(ver.value);
    lang && setLang(lang.value);
    !!categories.length &&
      setSelectedCategories(categories.map((category) => category.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilter]);

  useEffect(() => {
    setTomMultiSelectValue("list");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListOptions]);

  useEffect(() => {
    setTomMultiSelectValue("domain");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDomainOptions]);

  const setTomMultiSelectValue = (identifier) => {
    const select = document.getElementsByClassName(`js-select-${identifier}`);
    if (select.length) {
      const control = select?.[0].tomselect;
      if (control) {
        if (identifier === "list") {
          const listIds = selectedListOptions?.map((list) => list.value);
          control.items = listIds;
          const filteredLists = listsData?.filter((list) =>
            listIds.includes(list.uuid)
          );
          control.control.innerHTML = filteredLists
            .map(
              (list) =>
                `<div data-value="${list.uuid}" class="item" data-ts-item="">${list.name}<a data-value="${list.uuid}" href="javascript:void(0)" class="remove-list-item remove" tabIndex="-1" title="Remove">×</a></div>`
            )
            .join("");
        }

        if (identifier === "domain") {
          const domains = selectedDomainOptions?.map((domain) => domain.value);
          control.items = domains;
          const options = {};
          domains.forEach((domain) => {
            options[domain] = { text: domain, value: domain };
          });
          control.options = options;
          control.control.innerHTML = domains
            .map(
              (domain) =>
                `<div data-value="${domain}" class="item" data-ts-item="">${domain}<a data-value="${domain}" href="javascript:void(0)" class="remove-domain-item remove" tabIndex="-1" title="Remove">×</a></div>`
            )
            .join("");
        }
      }
    }
  };

  useEffect(() => {
    let elementsArray = document.querySelectorAll(".remove-list-item");
    elementsArray.forEach((elem) => {
      elem.addEventListener("click", (e) => {
        const listId = e.target.getAttribute("data-value");
        const filteredListOptions = selectedListOptions?.filter(
          (list) => list.value !== listId
        );
        const listIds = filteredListOptions?.map((list) => list.value);
        const filteredLists = listsData?.filter((list) =>
          listIds.includes(list.uuid)
        );
        const listOptions = filteredLists?.map((item) => ({
          value: item.uuid,
          label: item.name,
        }));
        setSelectedListOptions(listOptions);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedListOptions]);

  useEffect(() => {
    let elementsArray = document.querySelectorAll(".remove-domain-item");
    elementsArray.forEach((elem) => {
      elem.addEventListener("click", (e) => {
        const domainName = e.target.getAttribute("data-value");
        const filteredDomainOptions = selectedDomainOptions?.filter(
          (domain) => domain.value !== domainName
        );
        const domains = filteredDomainOptions?.map((domain) => domain.value);
        const domainOptions = domains?.map((domain) => ({
          value: domain,
          label: domain,
        }));
        setSelectedDomainOptions(domainOptions);
      });
    });
  }, [selectedDomainOptions]);

  let tooltipInit = false;
  useEffect(() => {
    if (!tooltipInit) {
      const tooltipTriggerList = document.querySelectorAll(
        '[data-bs-toggle="tooltip"]'
      );
      [...tooltipTriggerList].forEach(
        (tooltipTriggerEl) => new bootstrap.Tooltip(tooltipTriggerEl)
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
      tooltipInit = true;
    }
  }, []);

  const handleDomainSelectChange = (event) => {
    const options = Array.from(event.target.selectedOptions, (option) => ({
      value: option.value,
      label: option.label,
    }));
    setSelectedDomainOptions(options);
  };

  const handleListSelectChange = (event) => {
    const options = Array.from(event.target.selectedOptions, (option) => ({
      value: option.value,
      label: option.label,
    }));
    setSelectedListOptions(options);
  };

  const listSelectInitRef = useRef(null);
  useEffect(() => {
    if (!listSelectInitRef.current && listOptions.length) {
      HSCore.components.HSTomSelect.init(".js-select-list");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      listSelectInitRef.current = true;
    }
  }, [listOptions]);

  const domainSelectInitRef = useRef(null);
  useEffect(() => {
    if (!domainSelectInitRef.current) {
      HSCore.components.HSTomSelect.init(".js-select-domain");
      // eslint-disable-next-line react-hooks/exhaustive-deps
      domainSelectInitRef.current = true;
    }
  }, []);

  return (
    <div
      className="offcanvas offcanvas-end"
      tabIndex="-1"
      id="offcanvasEcommerceProductFilter"
      aria-labelledby="offcanvasEcommerceProductFilterLabel"
      aria-hidden="true"
    >
      <div className="offcanvas-header">
        <h4 id="offcanvasEcommerceProductFilterLabel" className="mb-0">
          {t("Filters")}
        </h4>
      </div>
      <div className="offcanvas-body">
        <div className="row">
          <div className="col-12">
            <span className="text-cap small mb-2">
              {t("Domain")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t(
                  "Domain name searches can be performed for both partial and full domain names. Additionally, it is possible to search for multiple names simultaneously"
                )}
              ></i>
            </span>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-12">
            <div className="tom-select-custom">
              <select
                className="js-select-domain form-select"
                autoComplete="off"
                multiple
                data-hs-tom-select-options='{
                  "create": true,
                  "placeholder": ""
                }'
                onChange={handleDomainSelectChange}
              >
              </select>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <span className="text-cap small mb-2">
              {t("List")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="You can filter by one or more lists"
              ></i>
            </span>
          </div>
        </div>
        <div className="row mb-3">
          <div className="col-12">
            <div className="tom-select-custom">
              {listOptions.length ? (
                <select
                  className="js-select-list form-select"
                  autoComplete="off"
                  multiple
                  data-hs-tom-select-options='{
                      "placeholder": ""
                    }'
                  onChange={handleListSelectChange}
                >
                  {listOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <span className="text-cap small">
              {t("Price")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Price")}
              ></i>
            </span>
          </div>
          <div className="col-6">
            <span className="text-cap small">
              {t("Publication Type")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Publication Type")}
              ></i>
            </span>
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="minPrice"
                placeholder="Min"
                aria-label="Min"
                value={minPrice}
                onChange={handleFilterChange(setMinPrice)}
              />
            </div>
            {errors.minPrice && (
              <small className="text-danger">{errors.minPrice}</small>
            )}
          </div>
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="maxPrice"
                placeholder="Max"
                aria-label="Max"
                value={maxPrice}
                onChange={handleFilterChange(setMaxPrice)}
              />
            </div>
            {errors.maxPrice && (
              <small className="text-danger">{errors.maxPrice}</small>
            )}
          </div>
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-pub"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={publicationType}
                onChange={handleFilterChange(setPublicationType)}
              >
                <option value="0">{t("Any")}</option>
                <option value="1">{t("Guest Post")}</option>
                <option value="2">{t("Link Insert")}</option>
                <option value="3">{t("Niche Edit")}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-6">
            <span className="text-cap small">
              {t("Domain Rating")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t(
                  "Domain Rating (DR) is a proprietary SEO metric by Ahrefs. It represents the strength of the website's backlink profile on a logarithmic scale from 0 to 100, with the latter being the strongest."
                )}
              ></i>
            </span>
          </div>
          <div className="col-6">
            <span className="text-cap small">
              {t("Domain Authority")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t(
                  "Domain Authority® (DA) is a search engine ranking score developed by Moz that predicts how likely a website is to rank in search engine result pages (SERPs). Domain Authority scores range from one to 100, with higher scores corresponding to greater likelihood of ranking."
                )}
              ></i>
            </span>
          </div>
        </div>
        <div className="row">
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="minDomainRating"
                placeholder="Min"
                aria-label="Min"
                min="1"
                max="100"
                value={minDomainRating}
                onChange={handleFilterChange(setMinDomainRating)}
              />
            </div>
            {errors.minDomainRating && (
              <small className="text-danger">{errors.minDomainRating}</small>
            )}
          </div>
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="maxDomainRating"
                placeholder="Max"
                aria-label="Max"
                min="1"
                max="100"
                value={maxDomainRating}
                onChange={handleFilterChange(setMaxDomainRating)}
              />
            </div>
            {errors.maxDomainRating && (
              <small className="text-danger">{errors.maxDomainRating}</small>
            )}
          </div>
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="minDomainAuthority"
                placeholder="Min"
                aria-label="Min"
                min="1"
                max="100"
                value={minDomainAuthority}
                onChange={handleFilterChange(setMinDomainAuthority)}
              />
            </div>
            {errors.minDomainAuthority && (
              <small className="text-danger">{errors.minDomainAuthority}</small>
            )}
          </div>
          <div className="col-3">
            <div>
              <input
                type="number"
                className="form-control"
                name="maxDomainAuthority"
                placeholder="Max"
                aria-label="Max"
                min="1"
                max="100"
                value={maxDomainAuthority}
                onChange={handleFilterChange(setMaxDomainAuthority)}
              />
            </div>
            {errors.maxDomainAuthority && (
              <small className="text-danger">{errors.maxDomainAuthority}</small>
            )}
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Turn Around Time")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Turn Around Time")}
              ></i>
            </small>
          </div>
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Traffic")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Traffic")}
              ></i>
            </small>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-tat"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={tat}
                onChange={handleFilterChange(setTat)}
              >
                <option value="0">{t("Any")}</option>
                <option value="24">{t("Up to 1 day")}</option>
                <option value="48">{t("Up to 2 days")}</option>
                <option value="72">{t("Up to 3 days")}</option>
                <option value="96">{t("Up to 4 days")}</option>
                <option value="120">{t("Up to 5 days")}</option>
                <option value="144">{t("Up to 6 days")}</option>
                <option value="168">{t("Up to 1 week")}</option>
              </select>
            </div>
          </div>
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-traffic"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={traffic}
                onChange={handleFilterChange(setTraffic)}
              >
                <option value="0">{t("Any")}</option>
                <option value="1000">&gt; 1K</option>
                <option value="5000">&gt; 5K</option>
                <option value="10000">&gt; 10K</option>
                <option value="50000">&gt; 50K</option>
                <option value="100000">&gt; 100K</option>
                <option value="250000">&gt; 250K</option>
                <option value="500000">&gt; 500K</option>
                <option value="1000000">&gt; 1M</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Link Type")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Link Type")}
              ></i>
            </small>
          </div>
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Niche")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Niche")}
              ></i>
            </small>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-rel"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={linkType}
                onChange={handleFilterChange(setLinkType)}
              >
                <option value="0">{t("Any")}</option>
                <option value={1}>{t("Do Follow")}</option>
                <option value={2}>{t("No Follow")}</option>
                <option value={3}>{t("Sponsored")}</option>
              </select>
            </div>
          </div>
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-niche"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={niche}
                onChange={handleFilterChange(setNiche)}
              >
                <option value="0">{t("Any")}</option>
                <option value={1}>{t("General")}</option>
                <option value={2}>{t("Gambling")}</option>
                <option value={3}>{t("Crypto")}</option>
                <option value={4}>{t("Medications")}</option>
                <option value={5}>{t("Dating")}</option>
                <option value={6}>{t("Adult")}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="row mt-3">
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Verification Status")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Verification Status")}
              ></i>
            </small>
          </div>
          <div className="col-6">
            <small className="text-cap text-body">
              {t("Language")}{" "}
              <i
                className="bi bi-exclamation-square"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title={t("Language")}
              ></i>
            </small>
          </div>
        </div>
        <div className="row">
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-ver"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={verificationStatus}
                onChange={handleFilterChange(setVerificationStatus)}
              >
                <option value="0">{t("Any")}</option>
                <option value="1">{t("Owner")}</option>
                <option value="2">{t("Contributor")}</option>
              </select>
            </div>
          </div>
          <div className="col-6">
            <div className="tom-select-custom">
              <select
                id="js-select-lang"
                className="js-select js-datatable-filter form-select form-select-sm"
                data-target-column-index="2"
                data-hs-tom-select-options='{
                  "placeholder": "Any",
                  "searchInDropdown": false,
                  "hideSearch": true,
                  "dropdownWidth": "10rem"
                }'
                value={lang}
                onChange={handleFilterChange(setLang)}
              >
                <option value="0">{t("Any")}</option>
                <option value="1">Arabic</option>
                <option value="2">Bulgarian</option>
                <option value="3">Chinese</option>
                <option value="4">Dutch</option>
                <option value="5">English</option>
                <option value="6">French</option>
                <option value="7">German</option>
                <option value="8">Greek</option>
                <option value="9">Hindi</option>
                <option value="10">Hrvatski</option>
                <option value="11">Indonesian</option>
                <option value="12">Italian</option>
                <option value="13">Japanese</option>
                <option value="14">Korean</option>
                <option value="15">Norwegian</option>
                <option value="16">Other</option>
                <option value="17">Polish</option>
                <option value="18">Portuguese</option>
                <option value="19">Romanian</option>
                <option value="20">Russian</option>
                <option value="21">Spanish</option>
                <option value="22">Swedish</option>
                <option value="23">Turkish</option>
                <option value="24">Ukrainian</option>
              </select>
            </div>
          </div>
        </div>

        <hr />

        <span className="text-cap small">{t("Domain Theme")}</span>

        <div className="row">
          <div className="col-6">
            <div className="d-grid gap-2 mb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="1"
                  id="productVendorFilterRadio1"
                  checked={selectedCategories.includes("1")}
                  onChange={handleCategoryChange("1")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio1"
                >
                  {t("Agriculture")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="2"
                  id="productVendorFilterRadio2"
                  checked={selectedCategories.includes("2")}
                  onChange={handleCategoryChange("2")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio2"
                >
                  {t("Animals and Pets")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="3"
                  id="productVendorFilterRadio3"
                  checked={selectedCategories.includes("3")}
                  onChange={handleCategoryChange("3")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio3"
                >
                  {t("Art")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="4"
                  id="productVendorFilterRadio4"
                  checked={selectedCategories.includes("4")}
                  onChange={handleCategoryChange("4")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio4"
                >
                  {t("Automobiles")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="5"
                  id="productVendorFilterRadio5"
                  checked={selectedCategories.includes("5")}
                  onChange={handleCategoryChange("5")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio5"
                >
                  {t("Business")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="6"
                  id="productVendorFilterRadio6"
                  checked={selectedCategories.includes("6")}
                  onChange={handleCategoryChange("6")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio6"
                >
                  {t("Books")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="7"
                  id="productVendorFilterRadio7"
                  checked={selectedCategories.includes("7")}
                  onChange={handleCategoryChange("7")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio7"
                >
                  {t("Beauty")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="8"
                  id="productVendorFilterRadio8"
                  checked={selectedCategories.includes("8")}
                  onChange={handleCategoryChange("8")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio8"
                >
                  {t("Career and Employment")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="9"
                  id="productVendorFilterRadio9"
                  checked={selectedCategories.includes("9")}
                  onChange={handleCategoryChange("9")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio9"
                >
                  {t("Computers")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="10"
                  id="productVendorFilterRadio10"
                  checked={selectedCategories.includes("10")}
                  onChange={handleCategoryChange("10")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio10"
                >
                  {t("Construction and Repairs")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="11"
                  id="productVendorFilterRadio11"
                  checked={selectedCategories.includes("11")}
                  onChange={handleCategoryChange("11")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio11"
                >
                  {t("Culture")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="12"
                  id="productVendorFilterRadio12"
                  checked={selectedCategories.includes("12")}
                  onChange={handleCategoryChange("12")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio12"
                >
                  {t("E-commerce")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="13"
                  id="productVendorFilterRadio13"
                  checked={selectedCategories.includes("13")}
                  onChange={handleCategoryChange("13")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio13"
                >
                  {t("Education")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="14"
                  id="productVendorFilterRadio14"
                  checked={selectedCategories.includes("14")}
                  onChange={handleCategoryChange("14")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio14"
                >
                  {t("Entertainment")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="15"
                  id="productVendorFilterRadio15"
                  checked={selectedCategories.includes("15")}
                  onChange={handleCategoryChange("15")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio15"
                >
                  {t("Environment")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="16"
                  id="productVendorFilterRadio16"
                  checked={selectedCategories.includes("16")}
                  onChange={handleCategoryChange("16")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio16"
                >
                  {t("Equipment")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="17"
                  id="productVendorFilterRadio17"
                  checked={selectedCategories.includes("17")}
                  onChange={handleCategoryChange("17")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio17"
                >
                  {t("Fashion")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="18"
                  id="productVendorFilterRadio18"
                  checked={selectedCategories.includes("18")}
                  onChange={handleCategoryChange("18")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio18"
                >
                  {t("Finance")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="19"
                  id="productVendorFilterRadio19"
                  checked={selectedCategories.includes("19")}
                  onChange={handleCategoryChange("19")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio19"
                >
                  {t("Food")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="20"
                  id="productVendorFilterRadio20"
                  checked={selectedCategories.includes("20")}
                  onChange={handleCategoryChange("20")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio20"
                >
                  {t("For Children")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="21"
                  id="productVendorFilterRadio21"
                  checked={selectedCategories.includes("21")}
                  onChange={handleCategoryChange("21")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio21"
                >
                  {t("For Men")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="22"
                  id="productVendorFilterRadio22"
                  checked={selectedCategories.includes("22")}
                  onChange={handleCategoryChange("22")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio22"
                >
                  {t("For Women")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="23"
                  id="productVendorFilterRadio23"
                  checked={selectedCategories.includes("23")}
                  onChange={handleCategoryChange("23")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio23"
                >
                  {t("Gadgets")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="24"
                  id="productVendorFilterRadio24"
                  checked={selectedCategories.includes("24")}
                  onChange={handleCategoryChange("24")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio24"
                >
                  {t("Games")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="25"
                  id="productVendorFilterRadio25"
                  checked={selectedCategories.includes("25")}
                  onChange={handleCategoryChange("25")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio25"
                >
                  {t("Hardware development")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="26"
                  id="productVendorFilterRadio26"
                  checked={selectedCategories.includes("26")}
                  onChange={handleCategoryChange("26")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio26"
                >
                  {t("Health")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="27"
                  id="productVendorFilterRadio27"
                  checked={selectedCategories.includes("27")}
                  onChange={handleCategoryChange("27")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio27"
                >
                  {t("Technology")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="28"
                  id="productVendorFilterRadio28"
                  checked={selectedCategories.includes("28")}
                  onChange={handleCategoryChange("28")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio28"
                >
                  {t("Home and Family")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="29"
                  id="productVendorFilterRadio29"
                  checked={selectedCategories.includes("29")}
                  onChange={handleCategoryChange("29")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio29"
                >
                  {t("Humour")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="30"
                  id="productVendorFilterRadio30"
                  checked={selectedCategories.includes("30")}
                  onChange={handleCategoryChange("30")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio30"
                >
                  {t("Internet")}
                </label>
              </div>
            </div>
          </div>

          <div className="col-6">
            <div className="d-grid gap-2 mb-2">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="31"
                  id="productVendorFilterRadio31"
                  checked={selectedCategories.includes("31")}
                  onChange={handleCategoryChange("31")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio31"
                >
                  {t("Law")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="32"
                  id="productVendorFilterRadio32"
                  checked={selectedCategories.includes("32")}
                  onChange={handleCategoryChange("32")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio32"
                >
                  {t("Leisure and Hobbies")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="33"
                  id="productVendorFilterRadio33"
                  checked={selectedCategories.includes("33")}
                  onChange={handleCategoryChange("33")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio33"
                >
                  {t("Lifestyle")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="34"
                  id="productVendorFilterRadio34"
                  checked={selectedCategories.includes("34")}
                  onChange={handleCategoryChange("34")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio34"
                >
                  {t("Literature")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="35"
                  id="productVendorFilterRadio35"
                  checked={selectedCategories.includes("35")}
                  onChange={handleCategoryChange("35")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio35"
                >
                  {t("Manufacturing")}
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="36"
                  id="productVendorFilterRadio36"
                  checked={selectedCategories.includes("36")}
                  onChange={handleCategoryChange("36")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio36"
                >
                  {t("Marketing and Advertising")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="37"
                  id="productVendorFilterRadio37"
                  checked={selectedCategories.includes("37")}
                  onChange={handleCategoryChange("37")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio37"
                >
                  {t("Miscellaneous")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="38"
                  id="productVendorFilterRadio38"
                  checked={selectedCategories.includes("38")}
                  onChange={handleCategoryChange("38")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio38"
                >
                  {t("Mobile")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="39"
                  id="productVendorFilterRadio39"
                  checked={selectedCategories.includes("39")}
                  onChange={handleCategoryChange("39")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio39"
                >
                  {t("Movies")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="40"
                  id="productVendorFilterRadio40"
                  checked={selectedCategories.includes("40")}
                  onChange={handleCategoryChange("40")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio40"
                >
                  {t("Music")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="41"
                  id="productVendorFilterRadio41"
                  checked={selectedCategories.includes("41")}
                  onChange={handleCategoryChange("41")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio41"
                >
                  {t("Nature")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="42"
                  id="productVendorFilterRadio42"
                  checked={selectedCategories.includes("42")}
                  onChange={handleCategoryChange("42")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio42"
                >
                  {t("News and Media")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="43"
                  id="productVendorFilterRadio43"
                  checked={selectedCategories.includes("43")}
                  onChange={handleCategoryChange("43")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio43"
                >
                  {t("Personal Blogs")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="44"
                  id="productVendorFilterRadio44"
                  checked={selectedCategories.includes("44")}
                  onChange={handleCategoryChange("44")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio44"
                >
                  {t("Photography")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="45"
                  id="productVendorFilterRadio45"
                  checked={selectedCategories.includes("45")}
                  onChange={handleCategoryChange("45")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio45"
                >
                  {t("Places")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="46"
                  id="productVendorFilterRadio46"
                  checked={selectedCategories.includes("46")}
                  onChange={handleCategoryChange("46")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio46"
                >
                  {t("Politics")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="47"
                  id="productVendorFilterRadio47"
                  checked={selectedCategories.includes("47")}
                  onChange={handleCategoryChange("47")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio47"
                >
                  {t("Programming")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="48"
                  id="productVendorFilterRadio48"
                  checked={selectedCategories.includes("48")}
                  onChange={handleCategoryChange("48")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio48"
                >
                  {t("Public Service")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="49"
                  id="productVendorFilterRadio49"
                  checked={selectedCategories.includes("49")}
                  onChange={handleCategoryChange("49")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio49"
                >
                  {t("Real Estate")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="50"
                  id="productVendorFilterRadio50"
                  checked={selectedCategories.includes("50")}
                  onChange={handleCategoryChange("50")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio50"
                >
                  {t("Science")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="51"
                  id="productVendorFilterRadio51"
                  checked={selectedCategories.includes("51")}
                  onChange={handleCategoryChange("51")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio51"
                >
                  {t("Shopping")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="52"
                  id="productVendorFilterRadio52"
                  checked={selectedCategories.includes("52")}
                  onChange={handleCategoryChange("52")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio52"
                >
                  {t("Society")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="53"
                  id="productVendorFilterRadio53"
                  checked={selectedCategories.includes("53")}
                  onChange={handleCategoryChange("53")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio53"
                  checked={selectedCategories.includes("53")}
                  onChange={handleCategoryChange("53")}
                >
                  {t("Software development")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="54"
                  id="productVendorFilterRadio54"
                  checked={selectedCategories.includes("54")}
                  onChange={handleCategoryChange("54")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio54"
                >
                  {t("Sports")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="55"
                  id="productVendorFilterRadio55"
                  checked={selectedCategories.includes("55")}
                  onChange={handleCategoryChange("55")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio55"
                >
                  {t("Startups")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="56"
                  id="productVendorFilterRadio56"
                  checked={selectedCategories.includes("56")}
                  onChange={handleCategoryChange("56")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio56"
                >
                  {t("Traveling")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="57"
                  id="productVendorFilterRadio57"
                  checked={selectedCategories.includes("57")}
                  onChange={handleCategoryChange("57")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio57"
                >
                  {t("Transport")}
                </label>
              </div>
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="productAvailabilityFilterRadio"
                  value="58"
                  id="productVendorFilterRadio58"
                  checked={selectedCategories.includes("58")}
                  onChange={handleCategoryChange("58")}
                />
                <label
                  className="form-check-label"
                  htmlFor="productVendorFilterRadio58"
                >
                  {t("Web-development")}
                </label>
              </div>
            </div>
          </div>
        </div>
        {/* eslint-disable-next-line */}
        <a href="javascript:;"
          className="link mt-2"
          onClick={handleClearCategories}
        >
          <i className="bi-x"></i> {t("Clear")}
        </a>
      </div>

      <div className="offcanvas-footer">
        <div className="row gx-2">
          <div className="col">
            <div className="d-grid">
              <button
                type="button"
                className="btn btn-white"
                onClick={handleClearFilters}
              >
                {t("Clear all filters")}
              </button>
            </div>
          </div>

          <div className="col">
            <div className="d-grid">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => handleApplyFilter()}
              >
                {t("Apply")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProductFilterModal;
